import React from "react";
import {
  FiEdit2,
  FiExternalLink,
  FiEye,
  FiImage,
  FiMail,
  FiMapPin,
  FiPhone,
  FiShoppingBag,
  FiTag,
  FiToggleLeft,
  FiToggleRight,
  FiTrash2,
} from "react-icons/fi";

import { toAbsolutePublicUrl } from "../../../../services/mediaLibraryServices";

import AdminButton from "../../_shared/ui/AdminButton";
import CommerceStatusBadge from "./CommerceStatusBadge";

import {
  formatDateTime,
  getCommerceTags,
  getGoogleMapsUrl,
  normalizeExternalUrl,
  truncateText,
} from "../utils/commercesAdminUtils";

export default function CommerceCard({
  item,
  actionId,
  onPreview,
  onEdit,
  onToggle,
  onAskDelete,
}) {
  const logoUrl = item?.logo_url ? toAbsolutePublicUrl(item.logo_url) : "";
  const photoUrl = item?.photo_url ? toAbsolutePublicUrl(item.photo_url) : "";
  const toggleLoading = actionId === `toggle-${item.id}`;
  const mapsUrl = getGoogleMapsUrl(item);
  const tags = getCommerceTags(item.tags);

  return (
    <article className="group overflow-hidden rounded-[1.7rem] border border-slate-200 bg-slate-50 transition hover:-translate-y-0.5 hover:border-orange-200 hover:bg-white hover:shadow-xl hover:shadow-slate-200/60 dark:border-slate-800 dark:bg-slate-950 dark:hover:border-orange-500/30 dark:hover:bg-slate-900 dark:hover:shadow-black/20">
      <div className="relative">
        <div className="relative h-44 overflow-hidden bg-slate-200 dark:bg-slate-800">
          {photoUrl ? (
            <img
              src={photoUrl}
              alt={item.nom_commerce}
              className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
            />
          ) : (
            <div className="flex h-full items-center justify-center bg-gradient-to-br from-orange-50 to-amber-100 text-orange-300 dark:from-slate-800 dark:to-slate-900 dark:text-slate-600">
              <FiShoppingBag className="text-5xl" />
            </div>
          )}

          <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-slate-950/45 via-slate-950/10 to-transparent" />

          <div className="absolute left-4 top-4 z-10">
            <CommerceStatusBadge active={!!item.is_active} />
          </div>
        </div>

        <div className="absolute bottom-0 left-5 z-20 flex h-24 w-24 translate-y-1/2 items-center justify-center overflow-hidden rounded-[1.6rem] border border-white/80 bg-white p-3 shadow-2xl ring-4 ring-white dark:border-slate-700 dark:bg-slate-900 dark:ring-slate-950">
          {logoUrl ? (
            <img
              src={logoUrl}
              alt={item.nom_commerce}
              className="max-h-full max-w-full object-contain"
            />
          ) : (
            <FiImage className="text-4xl text-slate-300 dark:text-slate-600" />
          )}
        </div>
      </div>

      <div className="flex min-h-full flex-col p-4 pt-16 sm:p-5 sm:pt-16">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            {item.categorie ? (
              <span className="inline-flex items-center gap-1.5 rounded-full border border-orange-200 bg-orange-50 px-2.5 py-1 text-xs font-bold text-orange-700 dark:border-orange-500/20 dark:bg-orange-500/10 dark:text-orange-300">
                <FiShoppingBag />
                {item.categorie}
              </span>
            ) : null}

            {mapsUrl ? (
              <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-xs font-bold text-amber-700 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-300">
                <FiMapPin />
                GPS
              </span>
            ) : null}
          </div>

          <h3 className="mt-3 line-clamp-2 text-lg font-bold leading-snug text-slate-950 dark:text-white">
            {item.nom_commerce || "Commerce sans nom"}
          </h3>

          {item.contact_nom ? (
            <p className="mt-1 line-clamp-1 text-sm font-bold text-slate-500 dark:text-slate-400">
              {item.contact_nom}
              {item.contact_fonction ? ` — ${item.contact_fonction}` : ""}
            </p>
          ) : null}
        </div>

        {tags.length ? (
          <div className="mt-4 flex flex-wrap gap-2">
            {tags.map((tag) => (
              <span
                key={tag}
                className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-3 py-1 text-xs font-bold text-slate-500 dark:bg-slate-800 dark:text-slate-400"
              >
                <FiTag />
                {tag}
              </span>
            ))}
          </div>
        ) : null}

        <p className="mt-4 line-clamp-3 text-sm leading-6 text-slate-600 dark:text-slate-300">
          {truncateText(item.description_commerce, 220) ||
            "Aucune description renseignée."}
        </p>

        <div className="mt-4 flex flex-wrap gap-2 text-xs font-bold text-slate-500 dark:text-slate-400">
          {item.tel ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-3 py-1.5 dark:bg-slate-800">
              <FiPhone />
              {item.tel}
            </span>
          ) : null}

          {item.mail ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-3 py-1.5 dark:bg-slate-800">
              <FiMail />
              {item.mail}
            </span>
          ) : null}

          {item.website ? (
            <a
              href={normalizeExternalUrl(item.website)}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 rounded-full bg-sky-50 px-3 py-1.5 text-sky-700 transition hover:bg-sky-100 dark:bg-sky-500/10 dark:text-sky-300"
            >
              <FiExternalLink />
              Site web
            </a>
          ) : null}

          {mapsUrl ? (
            <a
              href={mapsUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-3 py-1.5 text-amber-700 transition hover:bg-amber-100 dark:bg-amber-500/10 dark:text-amber-300"
            >
              <FiMapPin />
              Carte
            </a>
          ) : null}
        </div>

        <p className="mt-4 text-xs font-bold text-slate-400 dark:text-slate-500">
          Créé le {formatDateTime(item.created_at)}
        </p>

        <div className="mt-5 flex flex-wrap gap-2">
          <AdminButton
            icon={FiEye}
            variant="secondary"
            size="sm"
            onClick={() => onPreview(item.id)}
          >
            Voir
          </AdminButton>

          <AdminButton
            icon={FiEdit2}
            variant="soft"
            size="sm"
            onClick={() => onEdit(item.id)}
          >
            Modifier
          </AdminButton>

          <AdminButton
            icon={item.is_active ? FiToggleLeft : FiToggleRight}
            variant={item.is_active ? "secondary" : "primary"}
            size="sm"
            loading={toggleLoading}
            onClick={() => onToggle(item.id)}
          >
            {item.is_active ? "Masquer" : "Publier"}
          </AdminButton>

          <AdminButton
            icon={FiTrash2}
            variant="dangerSoft"
            size="sm"
            onClick={() => onAskDelete(item.id)}
          >
            Supprimer
          </AdminButton>
        </div>
      </div>
    </article>
  );
}